import React from 'react';
import { Spin } from 'antd';
import { BusinessItem } from '../../../services/businessService';

interface BusinessContentRendererProps {
    selectedBusinessTab: string;
    businessGuides: BusinessItem[];
    loading: boolean;
}

const BusinessContentRenderer: React.FC<BusinessContentRendererProps> = ({
    selectedBusinessTab,
    businessGuides,
    loading
}) => {
    
    
    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <p style={{ marginTop: '16px' }}>加载中...</p>
            </div>
        );
    }

    // 根据选中的标签获取对应的业务指南数据
    const getBusinessDataByTab = (tab: string) => {
        
        let rootTypes: string[];
        switch (tab) {
            case 'patent-process':
                rootTypes = ['ip_business_process'];
                break;
            case 'service-content':
                rootTypes = ['service_content'];
                break;
            case 'institution-process':
                rootTypes = ['institution_entry', 'institution_entry_process'];
                break;
            default:
                return [];
        }
        
        // 找到根节点（支持多种type值匹配）
        const rootItem = businessGuides.find(item => rootTypes.includes(item.type));
        if (!rootItem) {
            return [];
        }
        // 获取整个树形结构（根节点及其所有子节点）
        const getTreeData = (rootId: number): BusinessItem[] => {
            const result: BusinessItem[] = [];
            
            // 添加根节点
            const root = businessGuides.find(item => item.id === rootId);
            if (root) {
                result.push(root);
            }
            
            // 递归添加所有子节点
            const addChildren = (parentId: number) => {
                const children = businessGuides.filter(item => item.parentId === parentId);
                children.forEach(child => {
                    result.push(child);
                    addChildren(child.id!); // 递归添加子节点的子节点
                });
            };
            
            addChildren(rootId);
            return result;
        };
        
        const result = getTreeData(rootItem.id!);
        return result;
    };

    const currentBusinessData = getBusinessDataByTab(selectedBusinessTab);
    
    if (currentBusinessData.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <p>暂无数据</p>
            </div>
        );
    }

    // 构建树形结构
    const buildBusinessTree = (data: BusinessItem[]) => {
        const map = new Map<number, BusinessItem & { children: BusinessItem[] }>();
        const roots: (BusinessItem & { children: BusinessItem[] })[] = [];

        // 初始化所有节点
        data.forEach(item => {
            map.set(item.id!, { ...item, children: [] });
        });

        // 构建父子关系
        data.forEach(item => {
            const node = map.get(item.id!)!;
            if (item.parentId && map.has(item.parentId)) {
                map.get(item.parentId)!.children.push(node);
            } else {
                roots.push(node);
            }
        });

        return roots;
    };

    const businessTree = buildBusinessTree(currentBusinessData);
    
    // 获取根节点名称用于标题
    const getRootName = () => {
        if (businessTree.length > 0) {
            return businessTree[0].name;
        }
        switch (selectedBusinessTab) {
            case 'patent-process':
                return '知识产权业务办理流程';
            case 'service-content':
                return '服务内容';
            case 'institution-process':
                return '机构入驻流程';
            default:
                return '业务指南';
        }
    };
    
    const rootName = getRootName();
    const titleText = `${rootName}——业务指南`;

    // 渲染流程步骤
    const renderFlowSteps = (items: BusinessItem[]) => {
        return items.map((item, index) => (
            <React.Fragment key={item.id}>
                <div className="flow-box">
                    <div className="flow-box-header">
                        <div className="flow-number">{index + 1}</div>
                        <div className="flow-title">{item.name}</div>
                    </div>
                    <div className="flow-box-content">
                        <div className="flow-desc">{item.description || '暂无描述'}</div>
                        {item.content && (
                            <div className="flow-details">
                                <div className="flow-detail-item">{item.content}</div>
                            </div>
                        )}
                    </div>
                </div>
                {index < items.length - 1 && (
                    <div className="flow-connector horizontal">
                        <div className="flow-arrow-right"></div>
                    </div>
                )}
            </React.Fragment>
        ));
    };

    // 渲染服务卡片
    const renderServiceCards = (items: BusinessItem[]) => {
        return items.map(item => (
            <div key={item.id} className="service-card">
                <div className="service-icon">{item.icon || '📋'}</div>
                <h4>{item.name}</h4>
                <p>{item.description || '暂无描述'}</p>
                {item.children && item.children.length > 0 && (
                    <ul>
                        {item.children.map(child => (
                            <li key={child.id}>{child.name}</li>
                        ))}
                    </ul>
                )}
            </div>
        ));
    };

    // 渲染步骤卡片
    const renderStepCards = (items: BusinessItem[]) => {
        return items.map((item, index) => (
            <div key={item.id} className="step-card">
                <div className="step-number">{index + 1}</div>
                <div className="step-content">
                    <h4>{item.name}</h4>
                    <p>{item.description || '暂无描述'}</p>
                </div>
            </div>
        ));
    };

            switch (selectedBusinessTab) {
                case 'patent-process':
                    return (
                        <div className="business-content-section">
                            <div className="business-header">
                                <h1 className="business-header-title">
                                    {titleText}
                                </h1>
                                <div className="business-header-divider"></div>
                            </div>
                            <div className="flowchart-container">
                                {/* 为每个流程分类创建独立的流程图，流程分类作为主标题 */}
                                {businessTree.map(rootItem => 
                                    rootItem.children.map(processCategory => (
                                        <div key={processCategory.id} className="process-flowchart">
                                            <div className="flowchart-title">
                                                <div className="flowchart-icon">💡</div>
                                                <h4>{processCategory.name}</h4>
                                            </div>
                                            <div className="flowchart-steps horizontal">
                                                {renderFlowSteps(processCategory.children || [])}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    );
                case 'service-content':
                    return (
                        <div className="business-content-section">
                            <div className="business-header">
                                <h1 className="business-header-title">
                                    {titleText}
                                </h1>
                                <div className="business-header-divider"></div>
                            </div>
                            <div className="service-grid">
                                {/* 为每个服务分类创建独立的服务卡片 */}
                                {businessTree.map(rootItem => 
                                    rootItem.children.map(serviceCategory => (
                                        <div key={serviceCategory.id} className="service-card">
                                            <div className="service-icon">
                                                {serviceCategory.icon === 'icon-patent-service' ? '📋' :
                                                 serviceCategory.icon === 'icon-trademark-service' ? '🏷️' :
                                                 serviceCategory.icon === 'icon-copyright-service' ? '📄' :
                                                 serviceCategory.icon === 'icon-consultation' ? '💬' :
                                                 serviceCategory.icon || '📋'}
                                            </div>
                                            <h4>{serviceCategory.name}</h4>
                                            <p>{serviceCategory.description || '暂无描述'}</p>
                                            {serviceCategory.children && serviceCategory.children.length > 0 && (
                                                <ul>
                                                    {serviceCategory.children.map(child => (
                                                        <li key={child.id}>{child.name}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    );
                case 'institution-process':
                    return (
                        <div className="business-content-section">
                            <div className="business-header">
                                <h1 className="business-header-title">
                                    {titleText}
                                </h1>
                                <div className="business-header-divider"></div>
                            </div>
                            <div className="flowchart-container">
                                {/* 机构入驻流程：直接显示为水平流程图 */}
                                {businessTree.map(rootItem => (
                                    <div key={rootItem.id} className="process-flowchart">
                                        <div className="flowchart-title">
                                            <div className="flowchart-icon">🏢</div>
                                            <h4>{rootItem.name}</h4>
                                        </div>
                                        <div className="flowchart-steps horizontal">
                                            {renderFlowSteps(rootItem.children?.flatMap(category => category.children || []) || [])}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
        default:
            return <div>请选择业务指南类型</div>;
    }
};

export default BusinessContentRenderer;
